import { ServiceSessionOfferedEvent } from '@modules/ServiceModule';
import TasksModule from '@modules/TasksModule';
import InboxModule from '..';
import { setCallToOfferedWithSession } from '../callHandlers/callStateHandler';
import { InboxContext } from '../types';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
} from '@modules/DiagnosticsModule';
import { TaskState } from '@modules/TasksModule/types';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import getVaEscalationSummary from '@api/queries/getVaEscalationSummary';
function handleSessionOffered(
  context: InboxContext,
  event: ServiceSessionOfferedEvent,
) {
  if (event.userId !== context.userId) {
    return;
  }

  /*
   * We need to check if the task is already created by the softphone call event
   * If the session event comes after we fetch the pcsConversationSid we should
   * have the sessionSid on the task. If not, since the softphone event will not
   * have pcsConversationSid, we should use the source.address field to find the
   * task. This should either match the remoteIdentity or localIdentity depending
   * on if it's an external or internal call.
   */
  const task =
    TasksModule.getTask(event.sessionSid) ??
    TasksModule.getOfferedOrAcceptedTaskBySourceAddress(event.source?.address);

  if (task && task.state === TaskState.Offered) {
    // Task exists - emit latency metric
    const latencyMs = Date.now() - task.startedAt.getTime();
    context.logger.info(
      `[Latency Metric] handleSessionOffered - latency metric`,
      {
        taskId: event.sessionSid,
        latencyMs,
      },
    );
    DiagnosticsModule.recordHistogram(MetricNames.LATENCY_DURATION, latencyMs, {
      [MetricTags.OPERATION_NAME]: OperationNames.SESSION_OFFERED,
    });
  }

  // if there is no task and the source is not anonymous, we should offer the call (which handles task creation)
  if (!task && event.source.address !== 'anonymous') {
    // we need to create a task in pending state
    setCallToOfferedWithSession(context, {
      pcsConversationSid: event.sessionSid,
      from: event.from,
      displayName: event.source.displayName ?? event.from,
      sourceAddress: event.source.address,
      to: event.to,
    });
  }

  // we won't create a task if the source address is anonymous, so we still need to check before we set session props
  const taskId = task?.id ?? event.sessionSid;
  InboxModule.setSessionProperties(
    taskId,
    {
      // we either have the task or we just created it with sessionSid
      ...event,
      conversationId: event.sessionId,
      conversationSid: event.sessionSid,
    },
    event.to,
  );

  InboxModule.evaluateAndSetTaskType(taskId);

  // Fetch the VA escalation summary at offer time (keyed by conversationSid, the only id available
  // pre-accept) and attach it to the task so the incoming-call card can render it before accept. (VAH-1032)
  if (FeatureFlagModule.isEnabled(FeatureFlagKeys.featureVaEscalationSummary)) {
    getVaEscalationSummary(event.sessionSid)
      .then((escalationSummary) => {
        if (escalationSummary) {
          TasksModule.updateTaskAttributes(taskId, { escalationSummary });
        }
      })
      .catch((error) => {
        context.logger.error('Could not get VA escalation summary', { error });
      });
  }
}

export default handleSessionOffered;
